// src/services/ApiService.ts
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

// Define types locally since we can't import from api config
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// API endpoints configuration
const API_ENDPOINTS = {
  DASHBOARD: {
    STATS: '/dashboard/stats',
    USERS: '/dashboard/users',
    ACTIVITY: '/dashboard/activity',
    BLOCKCHAIN_STATUS: '/dashboard/blockchain/status',
    BLOCKCHAIN_TRANSACTIONS: '/dashboard/blockchain/transactions',
  },
  BUSINESS: {
    LIST: '/business',
    BASE: '/business',
  },
  USERS: {
    LIST: '/users',
  },
  SECURITY: {
    FRAUD_ALERTS: '/security/fraud-alerts',
    DASHBOARD: '/security/dashboard',
    SESSIONS: '/security/sessions',
    REPORT_FRAUD: '/security/report-fraud',
  },
  BATCHES: {
    LIST: '/batches',
    CREATE: '/batches',
    BASE: '/batches',
  },
  STORES: {
    LIST: '/stores',
    CREATE: '/stores',
    BASE: '/stores',
  },
  SUPPLY_CHAIN: {
    ANALYTICS: '/supply-chain/analytics',
    EVENTS: '/supply-chain/events',
    TRACK: '/supply-chain/track',
    BASE: '/supply-chain',
  },
  TAGS: {
    LIBRARIES: '/tags/libraries',
    HEALTH: '/tags/health',
    TEST: '/tags/test',
    IMPORT_SIMPLE: '/tags/library/simple-import',
    IMPORT_ENHANCED: '/tags/library/enhanced-import',
    SMART_DEBUG: '/tags/smart-debug',
    ADMIN_ACTIVATION: '/tags/admin/process-activation-request',
    BASE: '/tags',
  },
  REWARDS: {
    MY_REWARDS: '/rewards/my-rewards',
    MY_COUPONS: '/rewards/my-coupons',
    BASE: '/rewards',
    ISSUE: '/rewards/issue',
  },
  PRODUCTS: {
    LIST: '/products',
    BASE: '/products',
  },
  HEALTH: '/health',
};

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      //baseURL: process.env.REACT_APP_API_BASE_URL || '',
     
baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '',

      timeout: 10000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // ✅ Don’t retry 401/429; keep retries low
    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) =>
        axiosRetry.isNetworkOrIdempotentRequestError(error) &&
        error.response?.status !== 429 &&
        error.response?.status !== 401,
    });

    // ✅ Request interceptor: attach ONLY real token (no static fallbacks)
    this.axiosInstance.interceptors.request.use((config) => {
      const token =
        localStorage.getItem('auth_token') ||
        localStorage.getItem('authToken') ||
        sessionStorage.getItem('authToken') ||
        null;

      // ensure headers object exists
      (config.headers as any) = (config.headers as any) || {};

      if (token) {
        (config.headers as any).Authorization = `Bearer ${token}`;
      } else {
        delete (config.headers as any).Authorization;
      }

      // Optional: light log
      // console.log(`🌐 ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    });

    // ✅ Response interceptor: handle 401 + helpful diagnostics
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // console.error(`❌ API Error: ${error.response?.status} - ${error.config?.url}`, error?.message);

        if (error.message?.includes('CORS') || error.message?.includes('Network Error')) {
          // console.error('🚨 CORS/Network issue. Check backend URL/CORS/credentials.');
        }

        if (error.response?.status === 401) {
          this.removeToken();
          // Redirect to your business login route (adjust if different)
          window.location.href = '/business/login';
        }

        return Promise.reject(error);
      }
    );
  }

  // ---------- Debug ----------
  getConfig() {
    return {
      baseURL: this.axiosInstance.defaults.baseURL,
      withCredentials: this.axiosInstance.defaults.withCredentials,
      frontendURL: window.location.origin,
      environment: process.env.NODE_ENV,
      apiBaseUrlEnv: process.env.REACT_APP_API_BASE_URL,
    };
  }

  // ---------- Token management ----------
  setToken(token: string): void {
    localStorage.setItem('auth_token', token);  // legacy
    localStorage.setItem('authToken', token);   // current
    (this.axiosInstance.defaults.headers as any).Authorization = `Bearer ${token}`;
  }

  removeToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    if ((this.axiosInstance.defaults.headers as any)?.Authorization) {
      delete (this.axiosInstance.defaults.headers as any).Authorization;
    }
  }

  // ---------- Generic HTTP helpers ----------
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.delete(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.axiosInstance.patch(url, data, config);
    return response.data;
  }

  // ---------- Business ----------
  async getBusinesses(params?: any): Promise<PaginatedResponse<any>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);

    const url = `${API_ENDPOINTS.BUSINESS.LIST}?${searchParams.toString()}`;
    const response = await this.get<any[]>(url);

    return {
      ...response,
      pagination: {
        page: params?.page || 1,
        limit: params?.limit || 10,
        total: response.data?.length || 0,
        totalPages: Math.ceil((response.data?.length || 0) / (params?.limit || 10)),
      },
    };
  }

  async approveBusiness(businessId: string): Promise<ApiResponse<any>> {
    return this.post(`${API_ENDPOINTS.BUSINESS.BASE}/${businessId}/approve-direct`);
  }

  async rejectBusiness(businessId: string, reason?: string): Promise<ApiResponse<any>> {
    return this.post(`${API_ENDPOINTS.BUSINESS.BASE}/${businessId}/reject-direct`, { reason });
  }

  async updateBusinessStatus(businessId: string, status: string): Promise<ApiResponse<any>> {
    return this.patch(`${API_ENDPOINTS.BUSINESS.BASE}/${businessId}/status`, { status });
  }

  async suspendBusiness(businessId: string): Promise<ApiResponse<any>> {
    return this.updateBusinessStatus(businessId, 'suspended');
  }

  // ---------- Users ----------
  async getUsers(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.USERS.LIST);
  }

  // ---------- Security ----------
  async getFraudAlerts(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.SECURITY.FRAUD_ALERTS);
  }

  async getSecurityDashboard(): Promise<ApiResponse<any>> {
    return this.get(API_ENDPOINTS.SECURITY.DASHBOARD);
  }

  async getSessions(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.SECURITY.SESSIONS);
  }

  async reportFraud(fraudData: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.SECURITY.REPORT_FRAUD, fraudData);
  }

  // ---------- Batches ----------
  async getBatches(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.BATCHES.LIST);
  }

  async createBatch(batchData: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.BATCHES.CREATE, batchData);
  }

  async updateBatchStatus(id: string, status: string): Promise<ApiResponse<any>> {
    return this.put(`${API_ENDPOINTS.BATCHES.BASE}/${id}/status`, { status });
  }

  async recallBatch(id: string, reason: string): Promise<ApiResponse<any>> {
    return this.put(`${API_ENDPOINTS.BATCHES.BASE}/${id}/recall`, { reason });
  }

  // ---------- Stores ----------
  async getStores(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.STORES.LIST);
  }

  async createStore(storeData: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.STORES.CREATE, storeData);
  }

  async updateGPSSettings(id: string, settings: any): Promise<ApiResponse<any>> {
    return this.put(`${API_ENDPOINTS.STORES.BASE}/${id}/gps`, settings);
  }

  // ---------- Supply Chain ----------
  async getSupplyChainAnalytics(businessId: string): Promise<ApiResponse<any>> {
    return this.get(`${API_ENDPOINTS.SUPPLY_CHAIN.ANALYTICS}/${businessId}`);
  }

  async getSupplyEvents(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.SUPPLY_CHAIN.EVENTS);
  }

  async trackShipment(trackingId: string): Promise<ApiResponse<any>> {
    return this.get(`${API_ENDPOINTS.SUPPLY_CHAIN.TRACK}/${trackingId}`);
  }

  async updateLocation(id: string, location: { lat: number; lng: number }): Promise<ApiResponse<any>> {
    return this.put(`${API_ENDPOINTS.SUPPLY_CHAIN.BASE}/${id}/location`, location);
  }

  // ---------- Tags ----------
  async getTagLibraries(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.TAGS.LIBRARIES);
  }

  async getTagsHealth(): Promise<ApiResponse<any>> {
    return this.get(API_ENDPOINTS.TAGS.HEALTH);
  }

  async getTagsTest(): Promise<ApiResponse<any>> {
    return this.get(API_ENDPOINTS.TAGS.TEST);
  }

  async importTagsCSV(data: { csvData: string }): Promise<ApiResponse<any>> {
    try {
      return this.post(API_ENDPOINTS.TAGS.IMPORT_SIMPLE, data);
    } catch {
      return this.post(API_ENDPOINTS.TAGS.IMPORT_ENHANCED, data);
    }
  }

  async debugTag(tagId: string): Promise<ApiResponse<any>> {
    try {
      return this.get(`${API_ENDPOINTS.TAGS.SMART_DEBUG}/${tagId}`);
    } catch {
      return {
        success: true,
        data: {
          tagId,
          status: 'unknown',
          lastScan: null,
          debugInfo: 'Debug endpoint not available',
        },
        message: 'Debug data (mock)',
        error: undefined,
      } as ApiResponse<any>;
    }
  }

  async activateTag(tagId: string, data: { businessId: string }): Promise<ApiResponse<any>> {
    try {
      return this.post(API_ENDPOINTS.TAGS.ADMIN_ACTIVATION, { tagId, ...data });
    } catch {
      return this.post(`${API_ENDPOINTS.TAGS.BASE}/${tagId}/activate`, data);
    }
  }

  async scanTag(tagId: string): Promise<ApiResponse<any>> {
    try {
      return this.post(`${API_ENDPOINTS.TAGS.BASE}/${tagId}/scan`);
    } catch {
      return {
        success: true,
        data: {
          tagId,
          scanTime: new Date().toISOString(),
          verified: true,
          productInfo: 'Mock product data',
        },
        message: 'Scan completed (mock)',
        error: undefined,
      } as ApiResponse<any>;
    }
  }

  async importTagsSimple(data: { csvData: string }): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.TAGS.IMPORT_SIMPLE, data);
  }

  async importTagsEnhanced(data: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.TAGS.IMPORT_ENHANCED, data);
  }

  async debugSmartTag(tagId: string): Promise<ApiResponse<any>> {
    return this.get(`${API_ENDPOINTS.TAGS.SMART_DEBUG}/${tagId}`);
  }

  async processActivationRequest(data: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.TAGS.ADMIN_ACTIVATION, data);
  }

  // ---------- Rewards ----------
  async getMyRewards(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.REWARDS.MY_REWARDS);
  }

  async getMyCoupons(): Promise<ApiResponse<any[]>> {
    return this.get(API_ENDPOINTS.REWARDS.MY_COUPONS);
  }

  async redeemReward(id: string): Promise<ApiResponse<any>> {
    return this.post(`${API_ENDPOINTS.REWARDS.BASE}/${id}/redeem`);
  }

  async issueReward(rewardData: any): Promise<ApiResponse<any>> {
    return this.post(API_ENDPOINTS.REWARDS.ISSUE, rewardData);
  }
}

const apiService = new ApiService();
export default apiService;
export type { ApiResponse, PaginatedResponse };
